// The old non-sdk card design selection page is in ../pages/cardDesign/CardDesign.tsx

import {
  colors,
  ImpButtonProps,
  ImpSpacer,
  ImpText,
} from '@imprint-payments/imprint-ui';
import { useState } from 'react';

import { confirm } from '../../api/api';
import { useImpState } from '../../app/hooks';
import {
  CardDesignOption,
  CardDesignStatus,
  setCardImg,
} from '../../app/merchantDataSlice';
import CardDesignPicker, {
  CardDesignDisplay,
} from '../../components/cardDesign/CardDesignPicker';
import {
  emitSDKEvent,
  SDK_EVENTS,
  SDK_RESULTS,
  useSDKContext,
} from '../../utils/sdk';
import OnboardingLayout from './OnboardingLayout';

export interface CardDesignSelectionContent {
  title: string;
  subtitle?: string;
}

export interface CardDesignSelectionPageProps {
  content: CardDesignSelectionContent;
  cardDesignOptions: CardDesignOption[];
  onNext: () => void;
}

export default function CardDesignSelectionPage({
  content,
  cardDesignOptions,
  onNext,
}: CardDesignSelectionPageProps) {
  const { language, merchantData, consumerInfo, appScreenState, dispatch } =
    useImpState();

  const [selectedID, setSelectedID] = useState<string>(
    cardDesignOptions.find(
      (option) => option.status === CardDesignStatus.SELECTABLE
    )!.cardDesignID
  );

  const { setResult } = useSDKContext();

  const getCardDesignImage = (designID: string) => {
    return merchantData.cardDesignOptionsImages[designID]?.physical;
  };

  const handleContinue = () => {
    dispatch(
      confirm({
        token: consumerInfo.auth.token,
        applicationUUID: consumerInfo.auth.applicationUUID,
        cardDesignID: selectedID,
        sendSms: false,
        onSuccess: () => {
          setResult(SDK_RESULTS.OFFER_ACCEPTED);
          emitSDKEvent({
            event: SDK_EVENTS.OFFER_ACCEPTED,
          });
          dispatch(setCardImg(getCardDesignImage(selectedID)));
          onNext();
        },
      })
    );
  };

  const isCardDesignSelectable = () => {
    const selectedCard = cardDesignOptions.find(
      (design) => design.cardDesignID === selectedID
    )!;
    return selectedCard.status === CardDesignStatus.SELECTABLE;
  };

  const continueButtonState = () => {
    return appScreenState.loading
      ? 'submitting'
      : isCardDesignSelectable()
        ? 'enabled'
        : 'disabled';
  };

  const buttonProps: ImpButtonProps[] = [
    {
      children: language.continueBtn,
      variant: 'primary',
      buttonStyle: 'branded',
      state: continueButtonState(),
      onClick: handleContinue,
    },
  ];

  const cardDesignDisplays: CardDesignDisplay[] = cardDesignOptions.map(
    (option) => ({
      ...option,
      imgSrc: getCardDesignImage(option.cardDesignID) || '',
    })
  );

  return (
    <OnboardingLayout bottomButtons={buttonProps}>
      <ImpSpacer height="l" />
      <ImpText typography="headline3">{content.title}</ImpText>
      {content.subtitle && (
        <>
          <ImpSpacer height="xs" />
          <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
            {content.subtitle}
          </ImpText>
        </>
      )}
      <ImpSpacer height="2xl" />
      <CardDesignPicker
        cardDesigns={cardDesignDisplays}
        selectedID={selectedID}
        setSelectedID={setSelectedID}
      />
    </OnboardingLayout>
  );
}
